<template>
  <div class="restaurant-categories-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="restaurant-categories-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <div class="header-left">
                <h1 class="page-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="title-icon">
                    <path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z"/>
                  </svg>
                  Menu Categories
                </h1>
                <p class="page-subtitle">Organize your menu items into categories</p>
              </div>
              <div class="header-actions">
                <button @click="showAddModal = true" class="btn-primary">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                  </svg>
                  Add Category
                </button>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading categories...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="categories.length === 0" class="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" class="empty-icon">
              <path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z"/>
            </svg>
            <h3>No Categories Yet</h3>
            <p>Create your first menu category to get started!</p>
            <button @click="showAddModal = true" class="btn-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
              </svg>
              Create First Category
            </button>
          </div>

          <!-- Categories Grid -->
          <div v-else class="categories-grid">
            <div
              v-for="category in categories"
              :key="category.id"
              class="category-card"
            >
              <div class="category-header">
                <h4 class="category-name">{{ category.name }}</h4>
                <span v-if="category.name_ar" class="category-name-ar">{{ category.name_ar }}</span>
              </div>

              <p v-if="category.description" class="category-description">
                {{ category.description }}
              </p>

              <div class="category-footer">
                <span :class="['status-badge', { enabled: category.enabled }]">
                  {{ category.enabled ? 'Active' : 'Inactive' }}
                </span>

                <div class="category-actions">
                  <button @click="editCategory(category)" class="btn-icon" title="Edit">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
                    </svg>
                  </button>
                  <button @click="deleteCategory(category.id)" class="btn-icon btn-danger" title="Delete">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Add/Edit Category Modal -->
    <div v-if="showAddModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">{{ editingCategory ? 'Edit Category' : 'Add New Category' }}</h3>
          <button @click="closeModal" class="modal-close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label class="form-label">Category Name (English) *</label>
            <input
              v-model="categoryForm.name"
              type="text"
              placeholder="e.g., Pizza"
              class="form-input"
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label">Category Name (Arabic)</label>
            <input
              v-model="categoryForm.name_ar"
              type="text"
              placeholder="e.g., بيتزا"
              class="form-input"
              dir="rtl"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Description (English)</label>
            <textarea
              v-model="categoryForm.description"
              placeholder="Optional description..."
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>

          <div class="form-group">
            <label class="form-label">Description (Arabic)</label>
            <textarea
              v-model="categoryForm.description_ar"
              placeholder="وصف اختياري..."
              class="form-textarea"
              rows="3"
              dir="rtl"
            ></textarea>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input
                v-model="categoryForm.enabled"
                type="checkbox"
                class="form-checkbox"
              />
              <span>Active</span>
            </label>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn-secondary">Cancel</button>
          <button @click="saveCategory" class="btn-primary" :disabled="saving">
            {{ saving ? 'Saving...' : (editingCategory ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { authStore } from '@/store/auth'

export default {
  name: 'RestaurantCategoriesView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      loading: false,
      saving: false,
      showAddModal: false,
      editingCategory: null,
      categories: [],
      categoryForm: {
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        enabled: true
      }
    }
  },
  async mounted () {
    await this.checkRestaurantMode()
    this.loadCategories()
  },
  methods: {
    async checkRestaurantMode () {
      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant/mode/check', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          authStore.setRestaurantMode(data.restaurant_mode || false)
        }
      } catch (error) {
        console.error('Error checking restaurant mode:', error)
      }
    },

    async loadCategories () {
      try {
        this.loading = true
        const token = localStorage.getItem('auth_token')
        const response = await fetch('https://api.quali-d.com/restaurant-mode/categories', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await response.json()
        if (data.success) {
          this.categories = data.categories || []
        }
      } catch (error) {
        console.error('Error loading categories:', error)
        alert('Failed to load categories')
      } finally {
        this.loading = false
      }
    },

    async saveCategory () {
      if (!this.categoryForm.name.trim()) {
        alert('Please enter a category name')
        return
      }

      try {
        this.saving = true
        const token = localStorage.getItem('auth_token')
        const url = this.editingCategory
          ? `https://api.quali-d.com/restaurant-mode/categories/${this.editingCategory.id}`
          : 'https://api.quali-d.com/restaurant-mode/categories'

        const response = await fetch(url, {
          method: this.editingCategory ? 'PUT' : 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.categoryForm)
        })

        const data = await response.json()
        if (data.success) {
          alert('Category saved successfully!')
          this.closeModal()
          this.loadCategories()
        } else {
          alert('Failed to save category: ' + (data.message || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error saving category:', error)
        alert('Failed to save category')
      } finally {
        this.saving = false
      }
    },

    editCategory (category) {
      this.editingCategory = category
      this.categoryForm = { ...category }
      this.showAddModal = true
    },

    async deleteCategory (id) {
      if (!confirm('Are you sure you want to delete this category?')) return

      try {
        const token = localStorage.getItem('auth_token')
        const response = await fetch(`https://api.quali-d.com/restaurant-mode/categories/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        })

        const data = await response.json()
        if (data.success) {
          alert('Category deleted successfully!')
          this.loadCategories()
        } else {
          alert('Failed to delete category: ' + (data.message || 'Unknown error'))
        }
      } catch (error) {
        console.error('Error deleting category:', error)
        alert('Failed to delete category')
      }
    },

    closeModal () {
      this.showAddModal = false
      this.editingCategory = null
      this.categoryForm = {
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        enabled: true
      }
    }
  }
}
</script>

<style scoped>
.restaurant-categories-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 280px;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.restaurant-categories-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 32px;
}

.header-content {
  background: var(--bg-primary);
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.header-left {
  flex: 1;
}

.page-title {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 12px;
  transition: color 0.3s ease;
}

.title-icon {
  color: #3b82f6;
}

.page-subtitle {
  margin: 0;
  font-size: 15px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-primary);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
  transition: border-color 0.3s ease;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon {
  color: var(--text-tertiary);
  margin-bottom: 16px;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.empty-state p {
  margin: 0 0 24px 0;
  font-size: 14px;
  color: var(--text-secondary);
  transition: color 0.3s ease;
}

/* Categories Grid */
.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.category-card {
  background: var(--bg-primary);
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
}

.category-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.category-header {
  margin-bottom: 12px;
}

.category-name {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.category-name-ar {
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  direction: rtl;
  transition: color 0.3s ease;
}

.category-description {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
  transition: color 0.3s ease;
}

.category-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.status-badge.enabled {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.category-actions {
  display: flex;
  gap: 8px;
}

.btn-icon {
  background: none;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background: var(--bg-tertiary);
  color: #3b82f6;
}

.btn-icon.btn-danger:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Buttons */
.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--border-primary);
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
}

.btn-secondary:hover {
  background: var(--bg-tertiary);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  transition: background-color 0.3s ease;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 2px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  padding: 20px 24px;
  border-top: 2px solid var(--border-primary);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  transition: border-color 0.3s ease;
}

/* Form Elements */
.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
  transition: color 0.3s ease;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
  font-family: inherit;
  background: var(--bg-primary);
  color: var(--text-primary);
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-weight: 500;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.form-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .header-content {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .categories-grid {
    grid-template-columns: 1fr;
  }
}
</style>
